import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { fetchAllMonths } from '../../../services/monthlyAttendanceService';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import './LeavesReport.css';
import { leavesReport } from '../../../services/leaveService';
import { format } from 'date-fns';

const LeavesReport = () => {
    const [month, setMonth] = useState(null);
    const [year, setYear] = useState(null);
    const [errors, setErrors] = useState({});
    const [monthOptions, setMonthOptions] = useState([]);
    const yearInputRef = useRef(null);
    const [isFocused, setIsFocused] = useState({ month: false, year: false });
    const [leavesResponse, setLeavesResponse] = useState([]);

    const currentYear = new Date().getFullYear();
    const minDate = new Date(2000, 0, 1);
    const maxDate = new Date(currentYear, 11, 31);

    const handleYearChange = (date) => {
        const newYear = date ? date.getFullYear() : null;
        setYear(newYear);
        setMonth(null);
        setErrors((prev) => ({ ...prev, year: null }));
    };

    useEffect(() => {
        const loadMonths = async () => {
            try {
                const months = await fetchAllMonths(year);
                setMonthOptions(months);
            } catch (error) {
                console.error('Error fetching months:', error);
                setErrors((prev) => ({ ...prev, general: 'Failed to load months' }));
            }
        };
        loadMonths();
    }, [year]);

    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;
        switch (field) {
            case 'month':
                setMonth(value);
                break;
            default:
                break;
        }
        setErrors((prev) => ({ ...prev, [field]: null }));
    };

    const fetchLeavesReportResponse = async () => {
        try {
            const data = await leavesReport(year, month);
            setLeavesResponse(data);
        } catch (error) {
            console.error('Error fetching leaves report:', error);
            setErrors((prev) => ({ ...prev, general: 'Failed to load leave report' }));
        }
    };

    useEffect(() => {
        if (year && month) {
            fetchLeavesReportResponse();
        }
    }, [year, month]);

    // Function to download Excel file
    const downloadExcel = () => {
        if (leavesResponse.length === 0) {
            alert('No data available to download.');
            return;
        }

        const data = leavesResponse.map((leave, index) => ({
            'S.no': index + 1,
            'Employee Name': leave.employeeName,
            'From Date': format(leave.fromDate, 'dd-MM-yyyy'),
            'To Date': format(leave.toDate, 'dd-MM-yyyy'),
            'Days Applied': leave.daysApplied,
            'Leave Type': leave.type,
            Status: leave.status,
            Remarks: leave.remarks || 'N/A',
        }));

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Leaves Report');
        XLSX.writeFile(workbook, `Leaves_Report_${year}_${month}.xlsx`);
    };

    // Function to download PDF file
    const downloadPDF = () => {
        if (leavesResponse.length === 0) {
            alert('No data available to download.');
            return;
        }

        const doc = new jsPDF();
        doc.text('Leaves Report', 80, 20);
        doc.text(`Year: ${year} | Month: ${month}`, 14, 30);

        const tableData = leavesResponse.map((leave, index) => [
            index + 1,
            leave.employeeName,
            format(leave.fromDate, 'dd-MM-yyyy'),
            format(leave.toDate, 'dd-MM-yyyy'),
            leave.daysApplied,
            leave.type,
            leave.status,
            leave.remarks || 'N/A',
        ]);

        doc.autoTable({
            head: [['S.no', 'Employee Name', 'From Date', 'To Date', 'Days Applied', 'Leave Type', 'Status', 'Remarks']],
            body: tableData,
            startY: 40,
            theme: 'grid',
            headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255] },
            bodyStyles: { textColor: [0, 0, 0] },
        });

        doc.save(`Leaves_Report_${year}_${month}.pdf`);
    };

    return (
        <div>
            <div className="leaves-repo-box">
                <h2 className="leaves-repo-heading">Leaves Report</h2>
                {errors.general && <div className="error-message">{errors.general}</div>}

                <div className="leaves-repo-maincontainer">
                    <div className="leaves-repo-container-part">
                        <div className={`leaves-repo-floating-label-container ${isFocused.year || year ? 'float-label' : ''}`}>
                            <DatePicker
                                selected={year ? new Date(year, 0, 1) : null}
                                onChange={handleYearChange}
                                onFocus={() => setIsFocused((prev) => ({ ...prev, year: true }))}
                                onBlur={() => setIsFocused((prev) => ({ ...prev, year: false }))}
                                dateFormat="yyyy"
                                showYearPicker
                                placeholderText=""
                                className="epfWages-esic-date-picker"
                                minDate={minDate}
                                maxDate={maxDate}
                                ref={yearInputRef}
                            />
                            <label>
                                <span className="required">*</span>Year
                            </label>
                            {errors.year && <div className="error-message">{errors.year}</div>}
                        </div>
                    </div>
                    <div className="leaves-repo-container-part">
                        <div className={`leaves-repo-floating-label-container ${isFocused.month || month ? 'float-label' : ''}`}>
                            <Select
                                name="month"
                                options={monthOptions}
                                value={monthOptions.find((opt) => opt.value === month) || null}
                                onChange={(option) => handleInputChange(option, 'month')}
                                onFocus={() => setIsFocused((prev) => ({ ...prev, month: true }))}
                                onBlur={() => setIsFocused((prev) => ({ ...prev, month: false }))}
                                placeholder=" "
                                isClearable
                                menuPortalTarget={document.body}
                                styles={selectStyles}
                            />
                            <label>
                                <span className="required">*</span>Month
                            </label>
                            {errors.month && <div className="error-message">{errors.month}</div>}
                        </div>
                    </div>
                </div>



            </div>

            <div className="table-container">
                {leavesResponse.length > 0 ? (
                    <div className="download-buttons">
                    <button
                        onClick={downloadExcel}

                        className='xl-btnnn'
                    >
                        Download Excel
                    </button>
                    <button onClick={downloadPDF} className='pdf-btnnn'>Download PDF</button>
                    </div>) : (<div> </div>)
                }


                <table className="leaves-report-table">
                    <thead>
                        <tr>
                            <th>S.no</th>
                            <th>Employee Name</th>
                            <th>From Date</th>
                            <th>To Date</th>
                            <th>Days Applied</th>
                            <th>Leave Type</th>
                            <th>Status</th>
                            <th>Remarks</th>
                        </tr>
                    </thead>
                    <tbody>
                        {leavesResponse.length > 0 ? (
                            leavesResponse.map((leave, index) => (
                                <tr key={leave.id}>
                                    <td>{index + 1}</td>
                                    <td>{leave.employeeName}</td>
                                    <td>{format(leave.fromDate, 'dd-MM-yyyy')}</td>
                                    <td>{format(leave.toDate, 'dd-MM-yyyy')}</td>
                                    <td>{leave.daysApplied}</td>
                                    <td>{leave.type}</td>
                                    <td>{leave.status}</td>
                                    <td>{leave.remarks || 'N/A'}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'center' }}>
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LeavesReport;